import React, { useState } from 'react';
import './api-response-modal.css';
import useTranslation from '@/components/common/useTranslation.js';

import enMessages from '@/lang/en';
import arMessages from '@/lang/ar';

const DEFAULT_TITLE = "Sorry, something went wrong";
const DEFAULT_MESSAGE = "We couldn't connect to the server.<br>Please try again later.";

// Generic messages by API error code (translation keys)
const ERROR_MESSAGE_MAP = {
  // Success
  '000': {
    titleKey: 'api.error.000.title',
    messageKey: 'api.error.000.message',
  },
  // 4xx Client Errors
  '400': {
    titleKey: 'api.error.400.title',
    messageKey: 'api.error.400.message',
  },
  '401': {
    titleKey: 'api.error.401.title',
    messageKey: 'api.error.401.message',
  },
  '403': {
    titleKey: 'api.error.403.title',
    messageKey: 'api.error.403.message',
  },
  '404': {
    titleKey: 'api.error.404.title',
    messageKey: 'api.error.404.message',
  },
  '409': {
    titleKey: 'api.error.409.title',
    messageKey: 'api.error.409.message',
  },
  '410': {
    titleKey: 'api.error.410.title',
    messageKey: 'api.error.410.message',
  },
  '422': {
    titleKey: 'api.error.422.title',
    messageKey: 'api.error.422.message',
  },
  '423':{
    titleKey:'api.error.423.title',
    messageKey:'api.error.423.message',
  },
  // 5xx Server Errors
  '500': {
    titleKey: 'api.error.500.title',
    messageKey: 'api.error.500.message',
  },
  '510': {
    titleKey: 'api.error.510.title',
    messageKey: 'api.error.510.message',
  },
};

const ApiResponseModal = ({
  isOpen = false,
  type = 'error', // 'error' or 'success'
  title,
  message,
  buttonText = "common.ok",
  errorCode, // optional: API error code like "400", "409", "500", etc.
  errorDescription, // optional: error description for parsing (e.g., "Workflow Definition:123")
  onClose = () => {},
  zIndex = 9999, // Allow custom z-index to appear above other modals
  // Optional: standardized failed rows rendering (verification/import summary)
  failedRows, // Array<{ [key: string]: any }>
  failedSummary, // { totalRows?: number, successCount?: number, failedCount?: number }
  failedColumnOrder, // string[] preferred column order
  failedColumnLabels, // { [key: string]: string } label translation keys or literals
  failedRowsLimit = 5, // Only render first N failed rows to avoid huge tables
}) => {
  if (!isOpen) return null;

  const translate = useTranslation();

  // Parse error description for error code 410
  const parseErrorDescription = (description) => {
    if (!description || typeof description !== 'string') return null;
    
    // Expected format: "PageName:ReferenceNumber, PageName:ReferenceNumber" or "Page Name:123, Page Name:456"
    // Split by comma to handle multiple records
    const records = description.split(',').map(record => record.trim()).filter(Boolean);
    
    const parsedRecords = records.map(record => {
      const parts = record.split(':');
      if (parts.length >= 2) {
        return {
          pageName: parts[0].trim(),
          referenceNumber: parts.slice(1).join(':').trim()
        };
      }
      return null;
    }).filter(Boolean);
    
    return parsedRecords.length > 0 ? parsedRecords : null;
  };

  const parsedError = String(errorCode) === '410' ? parseErrorDescription(errorDescription || message) : null;
  
  // Debug logging
  if (errorCode) {
    console.log('ApiResponseModal - errorCode:', errorCode, 'type:', typeof errorCode);
    console.log('ApiResponseModal - errorDescription:', errorDescription);
    console.log('ApiResponseModal - parsedError:', parsedError);
  }

  // Manual translation lookup fallback for when context is missing
  const isRtl = typeof document !== 'undefined' && document.body.getAttribute('dir') === 'rtl';
  const messages = isRtl ? arMessages : enMessages;
  const staticTranslate = (id) => messages[id] || translate(id);

  const looksLikeTranslationKey = (val) => {
    if (!val || typeof val !== 'string') return false;
    const s = val.trim();
    // Heuristic: translation keys are usually dot-separated and have no spaces
    // e.g. "common.requestFailed", "api.error.400.message"
    if (!s) return false;
    if (s.includes(' ')) return false;
    if (!s.includes('.')) return false;
    return true;
  };

  const translatedTitle = looksLikeTranslationKey(title) ? staticTranslate(title) : title;
  const translatedMessage = looksLikeTranslationKey(message) ? staticTranslate(message) : message;

  let finalTitle = translatedTitle;
  let finalMessage = translatedMessage;
  const isSuccess = type === 'success';

  const hasCustomTitle = title !== undefined && title !== null && String(title).trim() !== '';
  const hasCustomMessage = message !== undefined && message !== null && String(message).trim() !== '';

  if (errorCode) {
    const mapped = ERROR_MESSAGE_MAP[String(errorCode)];
    if (mapped) {
      if (!hasCustomTitle) {
        finalTitle = staticTranslate(mapped.titleKey || '');
      }
      if (!hasCustomMessage) {
        finalMessage = staticTranslate(mapped.messageKey || '');
      }
    }
  }

  const defaultTitle = isSuccess
    ? (staticTranslate('common.success') || 'Success')
    : (staticTranslate('common.error') || DEFAULT_TITLE);
  const defaultMessage = isSuccess
    ? (staticTranslate('common.savedSuccessfully') || 'Saved successfully')
    : (staticTranslate('common.requestFailed') || DEFAULT_MESSAGE);

  if (!finalTitle || (looksLikeTranslationKey(title) && finalTitle === title)) {
    finalTitle = defaultTitle;
  }

  if (!finalMessage || (looksLikeTranslationKey(message) && finalMessage === message)) {
    finalMessage = defaultMessage;
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // State for copy tooltip
  const [copiedCell, setCopiedCell] = useState(null);

  // Copy to clipboard handler
  const handleCopy = (text, cellId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCell(cellId);
      setTimeout(() => setCopiedCell(null), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Render error 410 table
  const renderError410Table = () => {
    if (!parsedError || !Array.isArray(parsedError)) return null;
    
    return (
      <div className="error-410-table">
        <table className="error-410-table__table">
          <thead>
            <tr className="error-410-table__header-row">
              <th className="error-410-table__header-cell">{staticTranslate('api.error.410.pageName')}</th>
              <th className="error-410-table__header-cell">{staticTranslate('api.error.410.reference')}</th>
            </tr>
          </thead>
          <tbody>
            {parsedError.map((record, index) => (
              <tr key={index}>
                <td className="error-410-table__cell">
                  <div className="cell-with-copy">
                    <span className="cell-text">{record.pageName}</span>
                  </div>
                </td>
                <td className="error-410-table__cell">
                  <div className="cell-with-copy">
                    <span className="cell-text">{record.referenceNumber}</span>
                    <div className="copy-icon-wrapper">
                      <button
                        className="copy-icon-btn"
                        onClick={() => handleCopy(record.referenceNumber, `ref-${index}`)}
                        aria-label="Copy reference number"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      </button>
                      {copiedCell === `ref-${index}` && (
                        <span className="copy-tooltip">Copied!</span>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="api-response-overlay show" onClick={handleOverlayClick} onKeyDown={handleKeyDown} style={{ zIndex }}>
      <div className="api-response-modal-content">
        <div className="api-response-modal-body">
          <div className='success-message-modal'>
          <div className={`icon ${isSuccess ? 'success' : 'error'}`}>
            {isSuccess ? (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M8 8l8 8M16 8l-8 8M4 12h2m12 0h2M12 4v2m0 12v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            )}
          </div>
          <div className="title">{finalTitle}</div>
          <div className="msg" dangerouslySetInnerHTML={{ __html: finalMessage }}></div>
          {String(errorCode) === '410' && parsedError && renderError410Table()}
          {/* Standardized failed rows rendering (verification/import) */}
          {Array.isArray(failedRows) && failedRows.length > 0 && (
            <div className="failed-rows">
              {failedSummary && (
                <p className="import-summary-text">
                  {(staticTranslate('temporarySchedule.importSummary') || 'Summary')}:&nbsp;
                  {(staticTranslate('temporarySchedule.totalRows') || 'Total Rows')}: {failedSummary.totalRows ?? failedRows.length},&nbsp;
                  {(staticTranslate('temporarySchedule.success') || 'Success')}: {failedSummary.successCount ?? 0},&nbsp;
                  {(staticTranslate('temporarySchedule.failed') || 'Failed')}: {failedSummary.failedCount ?? failedRows.length}
                </p>
              )}
              <table className="failed-rows-table">
                <thead>
                  <tr className="failed-rows-table__header-row">
                    {(failedColumnOrder && failedColumnOrder.length
                      ? failedColumnOrder
                      : Object.keys(failedRows[0] || {})
                    ).map((col) => (
                      <th key={col} className="failed-rows-table__th">
                        {failedColumnLabels && failedColumnLabels[col]
                          ? staticTranslate(failedColumnLabels[col]) || failedColumnLabels[col]
                          : col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(failedRows.slice(0, Math.max(0, failedRowsLimit || 5))).map((row, idx) => (
                    <tr key={idx}>
                      {(failedColumnOrder && failedColumnOrder.length
                        ? failedColumnOrder
                        : Object.keys(failedRows[0] || {})
                      ).map((col) => (
                        <td key={col} className="failed-rows-table__td">{row?.[col] ?? ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
       
        <div className="actions">
          <button className="btn-ok" onClick={onClose}>
            {staticTranslate(buttonText)}
          </button>
        </div>
         </div>
      </div>
      </div>
    </div>
  );
};

export default ApiResponseModal;
